import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import MenuDropdownItem from './menuDropdownItem';
import Icon from '../common/containers/icon';
import checkCabinetPermissions from './headerUtils';
import processLegacyCabinets from './legacyCabinets';
import CabinetMenuItem from './cabinetMenuItem';
import DirectFormMenuItem from './directFormMenuItem';
import appHistory from '../../utils/appHistory';
const processCabinetType = (dashboards, user, groupedCabinets, ungroupedCabinets) => {
  const processCabinet = cabinet => {
    const identifier = cabinet.identifier,
      title = cabinet.title,
      path = cabinet.path,
      _cabinet$client = cabinet.client,
      _cabinet$client2 = _cabinet$client === void 0 ? {} : _cabinet$client,
      _cabinet$client2$web = _cabinet$client2.web,
      _cabinet$client2$web2 = _cabinet$client2$web === void 0 ? {} : _cabinet$client2$web,
      _cabinet$client2$web3 = _cabinet$client2$web2.layout,
      _cabinet$client2$web4 = _cabinet$client2$web3 === void 0 ? {} : _cabinet$client2$web3,
      superset = _cabinet$client2$web4.superset,
      _cabinet$client2$web5 = _cabinet$client2$web2.icon,
      icon = _cabinet$client2$web5 === void 0 ? 'folder-open' : _cabinet$client2$web5,
      _cabinet$client2$web6 = _cabinet$client2$web2.tabGroup,
      tabGroup = _cabinet$client2$web6 === void 0 ? {} : _cabinet$client2$web6,
      _cabinet$client2$web7 = _cabinet$client2$web2.sortOrder,
      sortOrder = _cabinet$client2$web7 === void 0 ? 0 : _cabinet$client2$web7,
      directFormType = cabinet.directFormType;
    const isValidDashboards = dashboards.some(dashboard => dashboard.name === superset);
    const isInvalidSuperset = superset && !isValidDashboards;
    const hasCabinetPermission = checkCabinetPermissions(cabinet, user);
    if (!isInvalidSuperset && hasCabinetPermission) {
      if (tabGroup.identifier) {
        if (!groupedCabinets[tabGroup.identifier]) {
          // eslint-disable-next-line no-param-reassign
          groupedCabinets[tabGroup.identifier] = [];
        }
        groupedCabinets[tabGroup.identifier].push({
          identifier,
          title,
          path,
          icon,
          sortOrder,
          directFormType
        });
      } else if (!tabGroup.identifier) {
        ungroupedCabinets.push({
          identifier,
          title,
          path,
          icon,
          sortOrder,
          directFormType
        });
      }
    }
  };
  return processCabinet;
};

// Helper function to get tab group info for a cabinet
const getTabGroupInfo = (tabGroupIdentifier, tabGroups) => {
  if (!tabGroups || !tabGroupIdentifier) return null;
  return tabGroups.find(group => group.identifier === tabGroupIdentifier);
};
const GroupDropdown = _ref => {
  let tabGroupIdentifier = _ref.tabGroupIdentifier,
    tabGroups = _ref.tabGroups,
    groupedCabinets = _ref.groupedCabinets;
  const groupInfo = getTabGroupInfo(tabGroupIdentifier, tabGroups);
  if (!groupInfo || !(groupedCabinets !== null && groupedCabinets !== void 0 && groupedCabinets.length)) return null;
  const sortedCabinets = groupedCabinets.sort((a, b) => a.sortOrder - b.sortOrder);
  const groupDropdownItems = sortedCabinets.map(cabinet => {
    if (cabinet.directFormType) {
      return /*#__PURE__*/React.createElement(DirectFormMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        directFormType: cabinet.directFormType
      });
    }
    // try to use CabinetMenuItem
    return /*#__PURE__*/React.createElement(MenuDropdownItem, {
      key: cabinet.identifier,
      className: "t-h-nav-".concat(cabinet.identifier),
      title: cabinet.title,
      glyph: cabinet.icon,
      href: "/#".concat(cabinet.path)
    });
  });
  return /*#__PURE__*/React.createElement(UncontrolledDropdown, {
    nav: true,
    innavbar: "true",
    className: "t-h-nav-".concat(tabGroupIdentifier, "-dropdown"),
    key: "".concat(tabGroupIdentifier, "Dropdown"),
    id: "".concat(tabGroupIdentifier, "DropdownMenu"),
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    nav: true,
    caret: true
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: groupInfo.icon || 'folder'
  }), /*#__PURE__*/React.createElement("br", null), groupInfo.title), /*#__PURE__*/React.createElement(DropdownMenu, null, groupDropdownItems));
};
const HierarchicalHeader = _ref2 => {
  let user = _ref2.user,
    _ref2$cabinets = _ref2.cabinets,
    cabinets = _ref2$cabinets === void 0 ? [] : _ref2$cabinets,
    _ref2$dashboards = _ref2.dashboards,
    dashboards = _ref2$dashboards === void 0 ? [] : _ref2$dashboards,
    _ref2$tabGroups = _ref2.tabGroups,
    tabGroups = _ref2$tabGroups === void 0 ? [] : _ref2$tabGroups,
    settingsLoaded = _ref2.settingsLoaded;
  const pathname = appHistory.location.pathname;
  const navMenuItems = [];

  // Group cabinets by their groups
  const groupedCabinets = {};
  const ungroupedCabinets = [];
  if (cabinets.length > 0) {
    const cabinetsWithPath = processLegacyCabinets(cabinets);
    cabinetsWithPath.forEach(processCabinetType(dashboards, user, groupedCabinets, ungroupedCabinets));
  }
  let firstCabinet = null;
  ungroupedCabinets.sort((a, b) => a.sortOrder - b.sortOrder).forEach(cabinet => {
    if (!firstCabinet) firstCabinet = cabinet;
    if (cabinet.directFormType) {
      navMenuItems.push(/*#__PURE__*/React.createElement(DirectFormMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        directFormType: cabinet.directFormType
      }));
    } else {
      navMenuItems.push(/*#__PURE__*/React.createElement(CabinetMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        path: cabinet.path
      }));
    }
  });
  const sortedGroupedCabinets = Object.entries(groupedCabinets).sort((a, b) => {
    const aValue = a[1].sortOrder;
    const bValue = b[1].sortOrder;
    return aValue - bValue;
  });
  sortedGroupedCabinets.forEach(tabGroup => {
    const tabGroupIdentifier = tabGroup[0];
    const tabGroupCabinets = tabGroup[1];
    if ((tabGroupCabinets === null || tabGroupCabinets === void 0 ? void 0 : tabGroupCabinets.length) > 0) {
      navMenuItems.push(/*#__PURE__*/React.createElement(GroupDropdown, {
        key: tabGroupIdentifier,
        tabGroupIdentifier: tabGroupIdentifier,
        tabGroups: tabGroups,
        groupedCabinets: tabGroupCabinets
      }));
    }
  });

  // Derive first-tab URL from cabinet data (avoids needing href on DirectFormMenuItem)
  let firstVisibleTab = null;
  if (firstCabinet) {
    firstVisibleTab = firstCabinet.directFormType ? "/directForms/new/".concat(firstCabinet.directFormType) : firstCabinet.path;
  }
  useEffect(() => {
    const isLandingRoute = pathname === '/';
    if (settingsLoaded && firstVisibleTab && isLandingRoute) {
      appHistory.replace(firstVisibleTab);
    }
  }, [pathname, settingsLoaded, firstVisibleTab]);
  return navMenuItems;
};
HierarchicalHeader.propTypes = {
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      general: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    path: PropTypes.string,
    denyPermissions: PropTypes.arrayOf(PropTypes.string),
    allowPermissions: PropTypes.arrayOf(PropTypes.string),
    client: PropTypes.shape({
      web: PropTypes.shape({
        layout: PropTypes.shape({
          superset: PropTypes.string
        }),
        icon: PropTypes.string,
        tabGroup: PropTypes.shape({
          identifier: PropTypes.string,
          sortOrder: PropTypes.number
        })
      })
    })
  })),
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string
  })),
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })),
  settingsLoaded: PropTypes.bool.isRequired
};
GroupDropdown.propTypes = {
  tabGroupIdentifier: PropTypes.string.isRequired,
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })).isRequired,
  groupedCabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })).isRequired
};
export default HierarchicalHeader;